import { useState, useEffect, useCallback } from 'react'
import { Box, Divider, HStack, Heading, Icon, VStack, Text } from '@chakra-ui/react'
import { FiList, FiLock } from 'react-icons/fi'
import SecurityContent from '@/components/Profile/SecurityContent'
import AuditLogContent from '@/components/Profile/AuditLogContent'
import { AuditLogEntry, AuditLogFilters, AuditLogPaginated } from '@/types'
import { apiService } from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

function getDefaultFilters(): AuditLogFilters {
  const today = new Date()
  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 7)
  return {
    date_from: weekAgo.toISOString().split('T')[0],
    date_to: today.toISOString().split('T')[0],
  }
}

export default function SecurityTab() {
  const { t } = useAppTranslation('common')
  const [filters, setFilters] = useState<AuditLogFilters>(getDefaultFilters)
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    apiService
      .getAuditLog(filters, page)
      .then((data: AuditLogPaginated) => {
        if (cancelled) return
        setEntries((prev) => (page === 1 ? data.data : [...prev, ...data.data]))
        setPagination({
          current_page: data.meta.current_page,
          last_page: data.meta.last_page,
          total: data.meta.total,
        })
      })
      .catch(() => {
        if (!cancelled) setEntries([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filters, page])

  const handleFilterChange = useCallback((newFilters: AuditLogFilters) => {
    setFilters(newFilters)
    setPage(1)
  }, [])

  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1)
  }, [])

  return (
    <VStack spacing={8} align="stretch">
      {/* Password */}
      <Box>
        <HStack spacing={2} mb={1}>
          <Icon as={FiLock} color="brand.500" boxSize={4} />
          <Heading size="sm">{t('profile.security.password')}</Heading>
        </HStack>
        <Text fontSize="sm" color="gray.500" mb={4}>
          {t('profile.security.pageSubtitle')}
        </Text>
        <SecurityContent />
      </Box>

      <Divider />

      {/* Audit log */}
      <Box>
        <HStack spacing={2} mb={1}>
          <Icon as={FiList} color="brand.500" boxSize={4} />
          <Heading size="sm">{t('profile.audit.pageTitle')}</Heading>
        </HStack>
        <Text fontSize="sm" color="gray.500" mb={4}>
          {t('profile.audit.pageSubtitle')}
        </Text>
        <AuditLogContent
          entries={entries}
          isLoading={isLoading}
          filters={filters}
          onFilterChange={handleFilterChange}
          pagination={pagination}
          onLoadMore={handleLoadMore}
        />
      </Box>
    </VStack>
  )
}
